import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { useDataProvider, useNotify } from "react-admin";

const BookingCommissionEditDialog = ({ open, handleClose, booking, onSuccess, onDelete }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const [formData, setFormData] = useState({
    feeAmount: "",
    feeAmountEmployee: "",
    feeRate: "",
    isFeePaid: false,
  });

  useEffect(() => {
    if (booking && booking.paymentInfo) {
      setFormData({
        feeAmount: booking.paymentInfo.feeAmount || "",
        feeAmountEmployee: booking.paymentInfo.feeAmountEmployee || "",
        feeRate: booking.paymentInfo.feeRate || "",
        isFeePaid: booking.paymentInfo.isFeePaid || false,
      });
    }
  }, [booking]);

  const handleChange = (field) => (event) => {
    const value = field === "isFeePaid" ? event.target.checked : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      const updateData = {
        paymentInfo: {
          ...booking.paymentInfo,
          feeAmount: parseFloat(formData.feeAmount) || 0,
          feeAmountEmployee: parseFloat(formData.feeAmountEmployee) || 0,
          feeRate: parseFloat(formData.feeRate) || 0,
          isFeePaid: formData.isFeePaid,
        },
      };

      await dataProvider.update("booking", {
        id: booking._id,
        data: updateData,
      });

      notify("Comisiones actualizadas con éxito", { type: "success" });
      handleClose();
      onSuccess();
    } catch (error) {
      notify("Error al actualizar las comisiones", { type: "error" });
    }
  };

  const handleDelete = async () => {
    try {
      await dataProvider.delete("booking", { id: booking._id });
      notify("Reserva eliminada con éxito", { type: "success" });
      handleClose();
      setConfirmDeleteOpen(false);
      onDelete();
    } catch (error) {
      notify("Error al eliminar la reserva", { type: "error" });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("es-ES");
  };

  if (!booking) return null;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Editar Comisiones de Servicio
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Información de la reserva (solo lectura) */}
            <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="h6" sx={{ mb: 2, color: "primary.main" }}>
                Información de la Reserva
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Cliente
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {booking.customer?.fullName || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Empleado
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {booking.employee?.fullName || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Fecha de Reserva
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDate(booking.bookingDate)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Precio Total
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Bs. {booking.paymentInfo?.totalPrice || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Servicios
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                    {booking.serviceCart?.map((item, index) => (
                      <Chip key={index} label={item.service?.name || "Servicio"} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Formulario editable de comisiones */}
            <Typography variant="h6" sx={{ mb: 2, color: "primary.main" }}>
              Información de Comisiones
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Tasa de Comisiones (%)"
                  type="number"
                  value={formData.feeRate}
                  onChange={handleChange("feeRate")}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  helperText="Porcentaje de comisión aplicado"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Comisiones Totales (Bs.)"
                  type="number"
                  value={formData.feeAmount}
                  onChange={handleChange("feeAmount")}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Total de comisiones del servicio"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Comisión para Empleado (Bs.)"
                  type="number"
                  value={formData.feeAmountEmployee}
                  onChange={handleChange("feeAmountEmployee")}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Comisión que recibe el empleado"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={formData.isFeePaid} onChange={handleChange("isFeePaid")} color="primary" />}
                  label="Comisión Pagada"
                  sx={{ mt: 2 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={handleClose} variant="outlined">
            Cancelar
          </Button>
          <Button onClick={() => setConfirmDeleteOpen(true)} color="error" variant="outlined">
            Eliminar Reserva
          </Button>
          <Button onClick={handleSave} variant="contained">
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación de eliminación */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        aria-labelledby="confirm-delete-title"
      >
        <DialogTitle id="confirm-delete-title">Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar esta reserva?
            <br />
            <br />
            <strong>Cliente:</strong> {booking?.customer?.fullName || "N/A"}
            <br />
            <strong>Empleado:</strong> {booking?.employee?.fullName || "N/A"}
            <br />
            <strong>Fecha:</strong> {formatDate(booking?.bookingDate)}
            <br />
            <strong>Total:</strong> Bs. {booking?.paymentInfo?.totalPrice || 0}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BookingCommissionEditDialog;
